import createClient from 'openapi-fetch';
import { handleUnauthorizedResponse } from '../containers/Organizations/OrganizationsSlice/unauthorizedHandler';
import { selectIsSwitchingTenantSession } from '../containers/Login/LoginSlice';
import { syncActiveTenantFromGlobalTokens } from '../containers/Organizations/OrganizationsSlice/upsertTenantSession';
import { store } from '../store';
import { getItem } from '../utils/storage';
import type { paths } from './coreApiSchema';
import { legacyLogoutRequest } from './legacySessions';
import { updateSession } from './sessions';
import { checkNetworkAvailability } from '../containers/CheckAppAvailable/helpers';
import { getServerConfig } from '../config/region';

export const onRequest = async ({ request }: { request: Request }): Promise<Request> => {
  const regionUrl = new URL(getServerConfig(getItem('selectedRegion')).coreApiUrl);
  const requestUrl = new URL(request.url);

  return new Request(
    new URL(requestUrl.pathname + requestUrl.search + requestUrl.hash, regionUrl),
    request
  );
};

// Stable API client. The destination is decided per-request by `onRequest` — so
// a region change is a state update (`setRegion`), not a client recreation.
// `baseUrl` is omitted on purpose: `onRequest` rewrites protocol + host to the
// current region's cluster.
export const coreApiClient = createClient<paths>({ baseUrl: 'http://localhost' });

coreApiClient.use({
  onRequest,
  async onResponse({ request, response }) {
    const clonedResponse = response.clone();

    const contentType = clonedResponse.headers.get('content-type');
    if (!contentType?.includes('application/json')) {
      // When 204 No Content
      return response;
    }

    const responseBody = await clonedResponse.json();

    const isRefreshEndpoint =
      request.url.includes('/core/v1/sessions/') && request.method.toUpperCase() === 'PUT';

    if (
      !isRefreshEndpoint &&
      isUnauthorizedResponse(responseBody) &&
      responseBody.error.code === 'EXPIRED_ACCESS_TOKEN'
    ) {
      const session = await updateSession({
        sessionId: getItem('coreApiSessionId'),
        refreshToken: getItem('coreApiRefreshToken'),
      });

      if (session.data) {
        syncActiveTenantFromGlobalTokens({
          accessToken: session.data.data.accessToken,
          refreshToken: session.data.data.refreshToken,
          sessionId: session.data.data.id,
        });

        const retryRequest = new Request(request, {
          headers: {
            ...Object.fromEntries(request.headers.entries()),
            authorization: `Bearer ${session.data.data.accessToken}`,
          },
        });

        return fetch(retryRequest);
      }
    }

    if (
      !isRefreshEndpoint &&
      (isUnauthorizedSessionResponse(responseBody) ||
        (isUnauthorizedResponse(responseBody) && responseBody.error.code === 'UNAUTHORIZED'))
    ) {
      // Skip during switchActiveOrganization: the switch owns its own rollback
      // and pruning. Running legacyLogoutRequest here would use the active
      // session's cookie (not the target tenant's), potentially invalidating
      // the wrong server-side session and causing a delayed full logout.
      if (selectIsSwitchingTenantSession(store.getState())) {
        return response;
      }
      try {
        // Wrapping legacy logout in try/finally since it can fail if e.g. user has logged out from another tab;
        // and we want to always clear web storage/user state.
        await legacyLogoutRequest();
      } finally {
        // Multi-org aware: falls back to the previous full `logoutUser` when
        // only one tenant session is cached; otherwise drops just the active
        // tenant and switches into a remaining one.
        await store.dispatch(handleUnauthorizedResponse());
      }
    }

    if (isInternalError(responseBody)) {
      checkNetworkAvailability();
    }

    return response;
  },
  async onError() {
    checkNetworkAvailability();
  },
});

export type Unauthorized =
  paths['/core/v1/tenants']['get']['responses']['401']['content']['application/json'];

export function isUnauthorizedResponse(arg: unknown | Unauthorized): arg is Unauthorized {
  if (typeof arg !== 'object' || arg === null) return false;

  const candidate = arg as Partial<Unauthorized>;

  return (
    candidate?.status === 'error' &&
    (candidate?.error?.code === 'EXPIRED_ACCESS_TOKEN' ||
      candidate?.error?.code === 'UNAUTHORIZED') &&
    candidate?.error?.httpStatus === 401
  );
}

export type UnauthorizedSession =
  paths['/core/v1/sessions/{id}']['put']['responses']['401']['content']['application/json'];

export function isUnauthorizedSessionResponse(
  arg: unknown | UnauthorizedSession
): arg is UnauthorizedSession {
  if (typeof arg !== 'object' || arg === null) return false;

  const candidate = arg as Partial<UnauthorizedSession>;

  return (
    candidate?.status === 'error' &&
    (candidate?.error?.code === 'EXPIRED_REFRESH_TOKEN' ||
      candidate?.error?.code === 'INVALID_REFRESH_TOKEN' ||
      candidate?.error?.code === 'INVALID_SESSION') &&
    candidate?.error?.httpStatus === 401
  );
}

type InternalError =
  paths['/core/v1/health']['get']['responses']['500']['content']['application/json'];

function isInternalError(arg: unknown): arg is InternalError {
  if (typeof arg !== 'object' || arg === null) return false;

  const candidate = arg as InternalError;

  return candidate?.status === 'error' && candidate?.error?.httpStatus === 500;
}
